import { buildHeaders } from '@/shared/redux/api/utils/auth';
import { handleRedirect } from '@/shared/redux/api/utils/handle-redirect';
import { extractOperationName } from '@/shared/redux/api/utils/operation-validators';
import { processResponse } from '@/shared/redux/api/utils/response-processor';
import { stripTypename } from '@/shared/utils/strip-typename';

import { handleError, handleFetchError } from './error-handlers';
import { batchedFetch } from './graphql-request-batcher';

export const stripTypenameFromGraphQLVariables = stripTypename;

/**
 * Executes a GraphQL query against a given URL and returns the processed result.
 * When extraHeaders are provided, the request is sent directly (not batched)
 * since per-request headers cannot be shared across batched requests.
 */
const executeQuery = async (
  targetUrl: string,
  document: string,
  variables: Record<string, any> | undefined,
  headers: Record<string, string>,
  extraHeaders?: Record<string, string>,
) => {
  const operationName = extractOperationName(document);
  const sanitizedVariables = stripTypenameFromGraphQLVariables(variables);

  if (extraHeaders) {
    const allHeaders = { ...headers, ...extraHeaders };
    const response = await fetch(targetUrl, {
      method: 'POST',
      headers: allHeaders,
      body: JSON.stringify({ query: document, variables: sanitizedVariables }),
    });
    handleRedirect(response);
    const jsonResult = await response.json();
    return processResponse(response, jsonResult, operationName);
  }

  const { response, jsonResult } = await batchedFetch(
    targetUrl,
    { query: document, variables: sanitizedVariables },
    headers,
  );

  return processResponse(response, jsonResult, operationName);
};

/**
 * HTTP status codes that indicate the endpoint itself is unreachable
 * or does not exist, warranting a fallback to the legacy URL.
 * Other 4xx/5xx errors (401, 403, 400, etc.) mean the endpoint
 * was reached and responded — those should fail immediately.
 */
const FALLBACK_STATUS_CODES = new Set([404, 500, 502, 503, 504]);

const shouldFallback = (result: { error?: any; data?: any }): boolean => {
  if (!result?.error) return false;

  const { status } = result.error;

  return typeof status === 'number' && FALLBACK_STATUS_CODES.has(status);
};

/**
 * Creates a GraphQL fetch base query function.
 *
 * Queries to the same URL are automatically batched within a short time
 * window (10ms) and sent as a single HTTP request to reduce browser
 * connection saturation.
 *
 * If a fallbackUrl is provided, the query will be retried against it
 * when the primary URL fails with a network or HTTP error (not GraphQL errors).
 *
 * @param url - The GraphQL endpoint URL
 * @param fallbackUrl - Optional fallback endpoint URL to try if the primary fails
 * @param requiresAuth - Whether authentication is required (default: true)
 * @param acceptCustomToken - Whether to accept a custom token from the Redux store (default: false)
 * @returns A base query function for Redux Toolkit Query
 */
export const createGraphQLFetchBaseQuery = (
  url: string,
  fallbackUrl?: string,
  requiresAuth = true,
  acceptCustomToken = false,
) => {
  return async (args: any, _api: any, _extraOptions: any) => {
    const { document, variables, extraHeaders } = args;

    const customToken = acceptCustomToken
      ? _api.getState().auth.backofficeTenantIdToken
      : undefined;

    try {
      const headers = await buildHeaders(requiresAuth, customToken);

      const result = await executeQuery(
        url,
        document,
        variables,
        headers,
        extraHeaders,
      );

      if (result?.error) {
        if (fallbackUrl && shouldFallback(result)) {
          try {
            const fallbackResult = await executeQuery(
              fallbackUrl,
              document,
              variables,
              headers,
              extraHeaders,
            );

            if (fallbackResult?.error) {
              return handleError(fallbackResult) || fallbackResult;
            }

            return fallbackResult;
          } catch {
            // Fallback also failed, return original error
          }
        }

        return handleError(result) || result;
      }

      return result;
    } catch (error) {
      if (fallbackUrl) {
        try {
          const headers = await buildHeaders(requiresAuth, customToken);
          const fallbackResult = await executeQuery(
            fallbackUrl,
            document,
            variables,
            headers,
            extraHeaders,
          );

          if (fallbackResult?.error) {
            return handleError(fallbackResult) || fallbackResult;
          }

          return fallbackResult;
        } catch {
          // Fallback also failed, return original error
        }
      }

      return handleFetchError(error);
    }
  };
};
